import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2, Lock } from "lucide-react";

const isValidEmail = (e) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(String(e || "").trim());

const EarlyAccess = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address", {
        description: "We need a working address to send investor updates.",
      });
      return;
    }
    setLoading(true);
    // Simulated submission (no storage per product requirement)
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      toast.success("You're on the list", {
        description: `We'll reach ${email} with investor updates and owner opportunities.`,
      });
      setEmail("");
    }, 700);
  };

  return (
    <section
      id="early-access"
      data-testid="early-access-section"
      className="relative isolate overflow-hidden border-y border-white/10 bg-neutral-950 py-24 md:py-32"
    >
      {/* Background flourish */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-orange-500/10 blur-[140px]" />
        <div className="absolute -right-24 bottom-0 h-[420px] w-[420px] rounded-full bg-yellow-500/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          {/* Left: copy */}
          <div className="lg:col-span-6">
            <p
              data-testid="ea-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.22em] text-orange-300"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
              Section 02 · Early Access
            </p>
            <h2
              data-testid="ea-headline"
              className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Fractional ownership of the<br />
              <span className="text-orange-400">grid that powers everything.</span>
            </h2>
            <p
              data-testid="ea-subcopy"
              className="mt-6 max-w-lg text-base leading-relaxed text-neutral-300"
            >
              Join the early list for investor updates, owner opportunities, and
              platform access. No spam. Unsubscribe in one click.
            </p>

            {/* Benefits */}
            <ul className="mt-8 space-y-3 text-sm text-neutral-300">
              {[
                "Fractional access to vetted infrastructure deals",
                "Quarterly thesis briefings & deal previews",
                "Direct line for tower & fiber owners",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`ea-benefit-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-orange-400" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form card */}
          <div className="lg:col-span-6">
            <div
              data-testid="ea-form-card"
              className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-neutral-900 to-neutral-950 p-1"
            >
              {/* glow border */}
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[conic-gradient(from_120deg,rgba(249,115,22,0.4),transparent_40%,rgba(234,179,8,0.35),transparent_70%,rgba(249,115,22,0.4))] opacity-60 blur-[2px]" />
              <div className="relative rounded-[22px] bg-neutral-950 p-8 sm:p-10">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400">
                    <Lock className="h-3.5 w-3.5 text-orange-400" />
                    Secured · Investor-only
                  </div>
                  <div className="font-mono text-[10px] text-neutral-500">
                    GV-EA-2026
                  </div>
                </div>

                <h3 className="font-display text-2xl font-semibold text-white">
                  Request access
                </h3>
                <p className="mt-1 text-sm text-neutral-400">
                  Enter your email — we’ll be in touch.
                </p>

                <form onSubmit={handleSubmit} noValidate className="mt-7 space-y-4" data-testid="ea-form">
                  <label
                    htmlFor="early-access-email"
                    className="block text-xs font-medium uppercase tracking-[0.18em] text-neutral-400"
                  >
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                    <Input
                      id="early-access-email"
                      data-testid="early-access-email-input"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@firm.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="gv-input h-14 rounded-2xl border-white/10 bg-neutral-900/70 pl-11 pr-4 text-base text-white placeholder:text-neutral-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="early-access-submit"
                    className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 text-sm font-semibold text-neutral-950 transition-all duration-300 hover:from-orange-400 hover:to-yellow-300 hover:shadow-[0_18px_60px_-12px_rgba(249,115,22,0.6)] disabled:opacity-70"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-950 border-t-transparent" />
                        Submitting…
                      </span>
                    ) : (
                      <>
                        Join Early Access
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  {success && (
                    <div
                      data-testid="ea-success"
                      className="flex items-center gap-2 rounded-xl border border-orange-500/20 bg-orange-500/5 px-4 py-3 text-sm text-orange-200"
                    >
                      <CheckCircle2 className="h-4 w-4 text-orange-400" />
                      You're on the list. Check your inbox soon.
                    </div>
                  )}

                  <p
                    data-testid="ea-disclaimer"
                    className="pt-2 text-[11px] leading-relaxed text-neutral-500"
                  >
                    Investment opportunities, if offered, will be made only
                    through compliant securities offerings and official offering
                    documents.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarlyAccess;
